/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage } from "@i18n/next";
import type { FlaggedIntention } from "@packages/sdk";
import { getFullName, useDate } from "@packages/sdk";
import type { ThemeType } from "@packages/ui";
import {
  Avatar,
  constantSemanticColors,
  gradientMapToV1,
  gradients,
  radius,
  spacing,
  stroke,
  Text,
} from "@packages/ui";
import type { HTMLAttributes } from "react";

const baseStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  borderColor: theme.semanticColors.neutralsLowest,
  borderRadius: radius.m,
  borderStyle: "solid",
  borderWidth: stroke.regular,
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
  padding: spacing.m,
});

const headerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};

const headerTextStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.t,
};

const headerTextTimeStyles: Interpolation<ThemeType> = (theme: ThemeType) => ({
  color: theme.semanticColors.neutralsMedium,
});

const textStyles = (props: { gradient: number }): Interpolation => ({
  background: gradients[props.gradient],
  borderRadius: radius.m,
  color: constantSemanticColors.onColorHighest,
  padding: spacing.l,
});

type ReportedCardIntentionProps = Omit<
  HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  data: FlaggedIntention;
};

export const ReportedCardIntention = ({
  data,
  ...props
}: ReportedCardIntentionProps) => {
  const { getDateFormatDifferenceNow } = useDate();

  return (
    <div css={baseStyles} {...props}>
      <div css={headerStyles}>
        <Avatar imgSrc={data.user.image_url} size={40} />
        <div css={headerTextStyles}>
          <Text size="xl" type="detail">
            <strong>
              {getFullName({
                firstName: data?.user.name,
                lastName: data?.user.last_name,
              })}
            </strong>
            &nbsp;
            <I18nMessage message="posted_an_intention" />
          </Text>
          <Text size="l" css={headerTextTimeStyles} type="detail">
            {getDateFormatDifferenceNow({ date: new Date(data.created_at) })}
          </Text>
        </div>
      </div>
      <Text
        as="p"
        size="l"
        type="body"
        css={[textStyles({ gradient: gradientMapToV1(data.gradient_id ?? 0) })]}
      >
        {data.text}
      </Text>
    </div>
  );
};

ReportedCardIntention.displayName = "ReportedCardIntention";
